// SPDX-License-Identifier: Apache-2.0
import { Collection, Feature, Map, MapBrowserEvent, MapEvent } from 'ol';
import { Circle, Fill, Icon, RegularShape, Stroke, Style } from 'ol/style';
import { ProjectionLike } from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { DragBox } from 'ol/interaction';
import { ScaleLine } from 'ol/control';
import { DragBoxEvent } from 'ol/interaction/DragBox';
import { Geometry, GeometryCollection, Point } from 'ol/geom';
import { Extent, getCenter, getHeight, getWidth } from 'ol/extent';

import SwipeManager from './tools/swipemanager';
import OsmManager from './tools/osmmanager';
import VectorTilesManager from './tools/vectortilesmanager';
import ViewManager from './tools/viewmanager';
import CogManager from './tools/cogmanager';
import DrawingManager from './tools/drawingmanager';

import GirafeHTMLElement from '../../base/GirafeHTMLElement';

import Basemap from '../../models/basemaps/basemap';
import Layer from '../../models/layers/layer';
import LayerCog from '../../models/layers/layercog';
import LayerXYZ from '../../models/layers/layerxyz';
import LayerOsm from '../../models/layers/layerosm';
import LayerVectorTiles from '../../models/layers/layervectortiles';
import LayerWmts from '../../models/layers/layerwmts';
import LayerWms from '../../models/layers/layerwms';
import LayerLocalFile from '../../models/layers/layerlocalfile';
import LayerDrawing from '../../models/layers/layerdrawing';
import GeoEvents from '../../models/events';
import MapPosition, { MapMarker } from '../../tools/state/mapposition';
import BaseLayer from '../../models/layers/baselayer';
import GroupLayer from '../../models/layers/grouplayer';
import { FocusFeature } from './tools/focusfeature';
import XyzManager from './tools/xyzmanager';
import ThemeLayer from '../../models/layers/themelayer';
import { isTimeAwareLayer } from '../../models/layers/timeawarelayer';
import { debounce } from '../../tools/utils/debounce';
import CircleStyle from 'ol/style/Circle';
import { parseCoordinates } from '../../tools/geometrytools';
import CircleGeom from 'ol/geom/Circle';
import WfsFilter from '../../tools/wfs/wfsfilter';
import WfsFilterCondition from '../../tools/wfs/wfsfiltercondition';
import { Callback } from '../../tools/state/statemanager';
import { applyOpacityToLayers } from '../../tools/utils/utils';
import { platformModifierKeyOnly } from 'ol/events/condition';
import { HighlightMode } from '../../tools/configuration/girafeconfig';
import { StyleFunction } from 'ol/style/Style';
import { FeatureLike } from 'ol/Feature';

export default class Map2dComponent extends GirafeHTMLElement {
  templateUrl = './template.html';
  styleUrls = ['../../../node_modules/ol/ol.css', '../../styles/common.css', './style.css'];

  olMap!: Map;
  mapTarget!: HTMLDivElement;
  swiper!: HTMLInputElement;
  closeSwiperButton!: HTMLButtonElement;
  swipeManager!: SwipeManager;
  osmManager!: OsmManager;
  cogManager!: CogManager;
  xyzManager!: XyzManager;
  drawingManager!: DrawingManager;
  viewManager!: ViewManager;
  vectorTilesManager!: VectorTilesManager;
  defaultSrid!: ProjectionLike;
  crosshairFeature!: Feature;
  crosshairLayer!: VectorLayer<VectorSource>;
  geolocationSource!: VectorSource;

  private readonly markerSource = new VectorSource();
  private readonly markerLayer = new VectorLayer({
    source: this.markerSource
  });
  private mapMarkerUnderCursor: FeatureLike | null = null;
  private previousMapCursorStyle = 'default';

  get projection() {
    return this.olMap.getView().getProjection();
  }

  get config() {
    return this.context.configManager.Config;
  }

  get onMobile() {
    return this.state.interface.isMobile;
  }

  /**
   *
   * @param path Overridden to make those methods public for this component
   * @param callback
   */
  public override subscribe(path: string, callback: Callback): Callback;
  public override subscribe(path: RegExp, callback: Callback): Callback;
  public override subscribe(path: string | RegExp, callback: Callback): Callback {
    // @ts-expect-error The call would have succeeded against this implementation,
    // but implementation signatures of overloads are not externally visible.
    return super.subscribe(path, callback);
  }

  // For object selection
  selectedFeaturesCollection: Collection<Feature<Geometry>> = new Collection();
  highlightedFeaturesCollection: Collection<Feature<Geometry>> = new Collection();
  selectionLayer!: VectorLayer<VectorSource>;
  selectionLayerStyle!: Style;
  selectionLayerStyleFunc: StyleFunction = (feature: FeatureLike) => {
    const shouldApplyCheck =
      this.config.selection.defaultHighlightMode === HighlightMode.HighlightPerLayer &&
      this.config.interface.defaultSelectionComponent === 'grid';
    if (shouldApplyCheck) {
      const selectedFeaturesType = this.state.selection.selectedFeaturesType;
      if (selectedFeaturesType && (feature.getId() as string).startsWith(selectedFeaturesType)) {
        return this.selectionLayerStyle;
      }
    } else {
      return this.selectionLayerStyle;
    }
  };
  highlightLayer!: VectorLayer<VectorSource>;
  pixelTolerance = 10;
  dragbox!: DragBox;
  focusFeature!: FocusFeature;

  mapTargetResizeObserver!: ResizeObserver;

  private get wmtsManager() {
    return this.context.wmtsManager;
  }

  public constructor(name = 'map-2d') {
    super(name);
  }

  /**
   * The Map needs to be displayed in Fullscreen Mode.
   */
  protected override get isFullscreenComponent(): boolean {
    return true;
  }

  resetAllSwipedLayers(layers: BaseLayer[]) {
    for (const layer of layers) {
      if (layer instanceof Layer) {
        layer.swiped = 'no';
      } else if (layer instanceof GroupLayer || layer instanceof ThemeLayer) {
        this.resetAllSwipedLayers(layer.children);
      }
    }

    this.swipeManager.deactivateSwiper();
  }

  registerEvents() {
    this.swiper.addEventListener('input', () => {
      this.olMap.render();
      this.updateCloseSwiperPosition();
    });

    this.closeSwiperButton.onclick = () => {
      this.resetAllSwipedLayers(this.state.layers.layersList);
    };

    this.subscribe('activeBasemaps', (_: Basemap[], newBasemaps: Basemap[]) => this.onChangeBasemaps(newBasemaps));
    this.subscribe(/activeBasemaps\.\d\.opacity/, (_oldOpacity: number, _newOpacity: number, basemap: Basemap) =>
      this.onChangeBasemapOpacity(basemap)
    );
    this.subscribe('projection', (oldProjection: string, newProjection: string) =>
      this.onChangeProjection(oldProjection, newProjection)
    );
    this.subscribe('interface.darkMapMode', () => this.onChangeDarkMode());
    this.subscribe('interface.fullscreenMode', (_oldValue: boolean, newValue: boolean) =>
      this.updateFullscreenButton(newValue)
    );
    this.subscribe('selection.selectedFeatures', (_oldFeatures: Feature[], newFeatures: Feature[]) =>
      this.onFeaturesSelected(newFeatures)
    );
    this.subscribe(
      'selection.selectedFeaturesType',
      (_oldFeaturesType: string | undefined, newFeaturesType: string | undefined) =>
        this.onFeaturesTypeChanged(newFeaturesType)
    );
    this.subscribe('selection.highlightedFeatures', (_oldFeatures: Feature[], newFeatures: Feature[]) =>
      this.onFeatureHighlighted(newFeatures)
    );
    this.subscribe('selection.focusedFeatures', (_oldFeature: Feature[] | null, newFeature: Feature[] | null) =>
      this.focusFeature.setFocusedFeatures(newFeature)
    );
    this.subscribe('position.markers', () => {
      this.clearAllMarkers();
      for (const marker of this.state.position.markers) {
        this.addMarker(marker);
      }
    });

    this.subscribe(
      /layers\.layersList\..*\.activeState/,
      async (_oldActive: boolean, _newActive: boolean, layer: Layer) => {
        await this.onLayerToggled(layer);
        if (layer.active) {
          this.onChangeSwiped(layer);
        }
      }
    );
    this.subscribe(/layers\.layersList\..*\.opacity/, (_oldOpacity: number, _newOpacity: number, layer: Layer) =>
      this.onChangeLayerOpacity(layer)
    );
    this.subscribe(/layers\.layersList\..*\.swiped/, (_oldOpacity: number, _newOpacity: number, layer: Layer) =>
      this.onChangeSwiped(layer)
    );
    this.subscribe(/layers\.layersList\..*\.filter/, (_oldFilter: string, _newFilter: string, layer: LayerWms) =>
      this.onChangeFilter(layer)
    );
    this.subscribe(
      /layers\.layersList\..*\.timeRestriction/,
      (_oldTime: string, _newTime: string, layer: GroupLayer | LayerWms) => this.onChangeTime(layer)
    );
    this.subscribe(/layers\.layersList\..*\.order/, () => this.onChangeOrder());

    this.subscribe('application.isReady', (_: boolean, isLoaded: boolean) => {
      if (isLoaded) {
        // The map component may not be fully ready when other part of the application will be initialized
        // And some operations that need a fuly loaded map need to be first done when the application is ready
        // For example, Permalink needs resolution,
        // If the app is opened from a permalink, prioritize this data over settings in the shared state
        if (this.context.permalinkManager.hasFeatureSelectionQuery()) {
          this.applyFeatureSelectionFromPermalink();
        } else if (this.context.permalinkManager.hasMapPosition()) {
          this.applyMapPositionFromPermalink();
        } else {
          this.applyFeatureSelectionFromSharedState();
        }
        this.showCrosshair(this.state.position);
        for (const marker of this.state.position.markers) {
          this.addMarker(marker);
        }
      }
    });
  }

  locateUser() {
    if ('geolocation' in navigator) {
      // The user's current position is utilized to show the distance to objects in the layer
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'granted' || result.state === 'prompt') {
          this.shadow.getElementById('disable-location')?.classList.remove('hidden');
          this.getCurrentLocation();
        }
      });
    } else {
      void window.gAlert('Geolocation browser error', 'Error');
    }
  }

  getCurrentLocation() {
    navigator.geolocation.getCurrentPosition(
      this.updateGeolocation,
      function (positionError) {
        switch (positionError.code) {
          case positionError.PERMISSION_DENIED:
            void window.gAlert('Geolocation permission denied', 'Error');
            break;
          case positionError.POSITION_UNAVAILABLE:
            void window.gAlert('Geolocation permission unavailable', 'Error');
            break;
          case positionError.TIMEOUT:
            void window.gAlert('Geolocation timeout', 'Error');
            break;
          default:
            void window.gAlert('Geolocation error', 'Error');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  }

  disableLocateUser = (): void => {
    this.geolocationSource.clear();
    this.shadow.getElementById('disable-location')?.classList.add('hidden');
  };

  /**
   * Toggles the application fullscreen mode.
   * The button is displayed over the map, like the geolocation button.
   */
  toggleFullscreen() {
    this.state.interface.fullscreenMode = !this.state.interface.fullscreenMode;
  }

  /**
   * Shows the matching fullscreen button (enter or exit) depending on the current mode.
   */
  private updateFullscreenButton(fullscreen: boolean) {
    this.shadow.getElementById('fullscreen-enter-button')?.classList.toggle('hidden', fullscreen);
    this.shadow.getElementById('fullscreen-exit-button')?.classList.toggle('hidden', !fullscreen);
  }

  readonly updateGeolocation = (position: GeolocationPosition): void => {
    const coords = position.coords;
    const longitude = coords.longitude;
    const latitude = coords.latitude;
    const accuracy = coords.accuracy; //Accuracy radius in meters

    const code = this.olMap.getView().getProjection().getCode();
    const maxExtent = this.config.map.maxExtent?.split(',').map(Number);
    const numbers = parseCoordinates([longitude, latitude], maxExtent, code);

    this.geolocationSource.clear();
    // point of location
    const positionFeature = new Feature({
      geometry: new Point(numbers),
      type: 'position'
    });
    this.geolocationSource.addFeature(positionFeature);

    // radius accuracy
    const circleGeometry = new CircleGeom(numbers, accuracy);
    const accuracyFeature = new Feature({
      geometry: circleGeometry,
      type: 'accuracy'
    });
    this.geolocationSource.addFeature(accuracyFeature);

    this.olMap.getView().setCenter(numbers);
  };

  override render() {
    super.render();

    // Read out the default projection from config, ignoring user preferences
    this.defaultSrid = this.context.configManager.getDefaultConfigValue('map.srid') as string;

    // Initialize the map element
    this.mapTarget = this.shadow.getElementById('ol-map') as HTMLDivElement;
    this.olMap.setTarget(this.mapTarget);

    // Initialize managers
    this.osmManager = new OsmManager(this.olMap);
    this.cogManager = new CogManager(this.olMap);
    this.xyzManager = new XyzManager(this.olMap);
    this.drawingManager = new DrawingManager(this.olMap);
    this.viewManager = new ViewManager(this.olMap, this.context.configManager, this.context.stateManager);
    this.vectorTilesManager = new VectorTilesManager(this.olMap);
    this.swiper = this.shadow.getElementById('swiper') as HTMLInputElement;
    this.closeSwiperButton = this.shadow.getElementById('close-swiper') as HTMLButtonElement;
    this.swipeManager = new SwipeManager(
      this.olMap,
      this.swiper,
      this.closeSwiperButton,
      this.wmtsManager,
      this.context.wmsManager,
      this.context.localFileManager
    );

    // View
    const view = this.viewManager.getDefaultView();
    this.olMap.setView(view);

    // Create layer for highlighted features
    const highlightSource = new VectorSource({
      features: this.highlightedFeaturesCollection
    });

    // Create layer for selection
    const selectionSource = new VectorSource({
      features: this.selectedFeaturesCollection
    });

    this.selectionLayer = new VectorLayer({
      properties: {
        addToPrintedLayers: true
      },
      source: selectionSource,
      style: this.selectionLayerStyleFunc
    });
    this.highlightLayer = new VectorLayer({
      source: highlightSource
    });
    this.setSelectLayerStyle();
    this.setHighlightLayerStyle();
    this.olMap.addLayer(this.selectionLayer);
    this.olMap.addLayer(this.highlightLayer);
    this.olMap.addLayer(this.markerLayer);
    this.selectionLayer.setZIndex(1002);
    this.highlightLayer.setZIndex(1003);
    this.selectionLayer.set('altitudeMode', 'clampToGround');

    if (this.config.map.showScaleLine) {
      const scaleLine = new ScaleLine({
        units: 'metric'
      });
      this.olMap.addControl(scaleLine);
    }

    //layer user location
    this.geolocationSource = new VectorSource();
    const geolocationLayer = new VectorLayer({
      source: this.geolocationSource,
      style: new Style({
        image: new CircleStyle({
          radius: 7,
          fill: new Fill({
            color: 'rgba(225,18,18,0.48)'
          }),
          stroke: new Stroke({
            color: 'rgb(225,18,18)',
            width: 2
          })
        }),
        fill: new Fill({
          color: 'rgba(20,100,213,0.49)'
        }),
        stroke: new Stroke({
          color: 'rgb(20,100,213)',
          width: 1
        })
      })
    });
    this.olMap.addLayer(geolocationLayer);

    // TODO REG: This is ugly, but I didn't find any other solution yet.
    setTimeout(() => {
      this.olMap.updateSize();
    }, 1000);

    this.mapTargetResizeObserver = new ResizeObserver(() => {
      // The size of the Map is defined by the Cell of the Grid it is displayed in,
      // and the user can change it at any time by dragging a splitter.
      this.olMap.updateSize();
      this.updateCloseSwiperPosition();
    });
    this.mapTargetResizeObserver.observe(this.mapTarget);
  }

  listenOpenLayersEvents() {
    // https://openlayers.org/en/latest/apidoc/module-ol_Map-Map.html
    //this.olMap.on('change', (e) => console.log(e));
    //this.olMap.on('click', (e) => console.log(e));
    //this.olMap.on('dblclick', (e) => console.log(e));
    //this.olMap.on('error', (e) => console.log(e));
    this.olMap.on('loadstart', (e) => this.onLoadStart(e));
    this.olMap.on('loadend', (e) => this.onLoadEnd(e));
    //this.olMap.on('movestart', (e) => console.log(e));
    //this.olMap.on('pointerdrag', (e) => console.log(e));
    //this.olMap.on('postcompose', (e) => console.log(e));
    //this.olMap.on('postrender', (e) => console.log(e));
    //this.olMap.on('precompose', (e) => console.log(e));
    //this.olMap.on('propertychange', (e) => console.log(e));
    //this.olMap.on('rendercomplete', (e) => console.log(e));
    //? change:layerGroup
    //? change:size
    //? change:target
    //? change:view

    // Register all mouse and keyboard interactions with the UserInteractionManager as non-exclusive
    // events. If another tool registers one of these events exclusively, the listeners bellow will
    // be paused temporarily and reactivate once the tool is closed.

    // Select features via GetFeatureInfo
    if (this.registerInteractionListener('map.select', false)) {
      // Simple click for single feature selection
      this.olMap.on('singleclick', (e) => {
        if (this.mapMarkerUnderCursor) {
          const mapMarker = this.mapMarkerUnderCursor.get('mapMarker') as MapMarker;
          if (mapMarker) {
            this.state.position.tooltip = {
              title: mapMarker.tooltip?.title,
              content: mapMarker.tooltip?.content ?? '',
              position: mapMarker.position
            };
            return;
          }
        }
        if (this.canExecute('map.select')) {
          this.onClick(e as MapBrowserEvent<PointerEvent>);
        }
      });

      // Dragbox interaction for multiple features selection
      this.dragbox = new DragBox({
        condition: (e) => platformModifierKeyOnly(e) && this.canExecute('map.select')
      });
      this.olMap.addInteraction(this.dragbox);
      this.dragbox.on('boxend', (e) => this.onDragSelection(e));
    }

    if (this.registerInteractionListener('map.mousemove', false)) {
      // Current map position
      this.olMap.on('moveend', (e) => {
        if (this.canExecute('map.mousemove')) {
          this.onMoveEnd(e as MapBrowserEvent<PointerEvent>);
        }
      });
      // Cursor coordinates
      this.olMap.on('pointermove', (e) => {
        if (this.canExecute('map.mousemove')) {
          this.onPointerMove(e as MapBrowserEvent<PointerEvent>);
        }
      });
    }
  }

  onLoadStart(_e: MapEvent) {
    this.state.loading = true;
  }

  onLoadEnd(_e: MapEvent) {
    this.state.loading = false;
  }

  onPointerMove(e: MapBrowserEvent<PointerEvent>) {
    this.state.mouseCoordinates = e.coordinate;

    const featuresAtPixel = this.olMap.getFeaturesAtPixel(e.pixel, {
      layerFilter: (layer) => layer === this.markerLayer
    });

    if (featuresAtPixel.length > 0) {
      this.mapMarkerUnderCursor = featuresAtPixel[0];
      const mapMarker = this.mapMarkerUnderCursor.get('mapMarker') as MapMarker;
      if (mapMarker?.tooltip && this.mapTarget.style.cursor !== 'pointer') {
        this.previousMapCursorStyle = this.mapTarget.style.cursor;
        this.mapTarget.style.cursor = 'pointer';
      }
    } else {
      this.mapMarkerUnderCursor = null;
      this.mapTarget.style.cursor = this.previousMapCursorStyle;
    }
  }

  onMoveEnd(_e: MapEvent) {
    const view = this.olMap.getView();

    const newPosition = this.state.position.clone();
    newPosition.center = view.getCenter()!;
    newPosition.zoom = view.getZoom()!;
    newPosition.resolution = view.getResolution()!;
    newPosition.scale = this.viewManager.getScale();

    if (newPosition.isValid) {
      this.state.position = newPosition;
    }
  }

  onClick(e: MapBrowserEvent<PointerEvent>) {
    // Build the selection box using the default tolerance. Note that the origin of the pixel coordinates is in
    // the top left corner, whereas the origin of the map coordinates is in the bottom left corner.
    // To get a valid bounding box [xMin, yMin, xMax, yMax] is needed.
    const lowerLeftPixel = [e.pixel[0] - this.pixelTolerance, e.pixel[1] + this.pixelTolerance];
    const lowerLeftCoord = this.olMap.getCoordinateFromPixel(lowerLeftPixel);
    const topRightPixel = [e.pixel[0] + this.pixelTolerance, e.pixel[1] - this.pixelTolerance];
    const topRightCoord = this.olMap.getCoordinateFromPixel(topRightPixel);
    this.select([lowerLeftCoord[0], lowerLeftCoord[1], topRightCoord[0], topRightCoord[1]]);
  }

  onDragSelection(_e: DragBoxEvent) {
    const extent = this.dragbox.getGeometry().getExtent();
    this.select(extent);
  }

  select(extent: number[]) {
    this.context.selectionManager.select(extent);
  }

  selectNone() {
    this.context.selectionManager.selectNone();
  }

  protected override connectedCallback() {
    super.connectedCallback();
    this.olMap = this.context.mapManager.getMap();
    this.focusFeature = new FocusFeature(this.olMap, this.context.configManager);
    this.render();
    this.listenOpenLayersEvents();
    this.registerEvents();
  }

  onCustomGirafeEvent(details: { action: string; layer: Layer; extent: Extent }) {
    if (details.action === GeoEvents.zoomToExtent) {
      this.zoomToExtent(details.extent);
    }
  }

  onChangeSwiped(layer: Layer) {
    this.swipeManager.toggleSwipe(layer);
    this.updateCloseSwiperPosition();
  }

  /**
   * updates icon position when swipe is moved
   */
  updateCloseSwiperPosition() {
    const sliderValue = Number.parseFloat(this.swiper.value);
    const max = Number.parseFloat(this.swiper.max);
    const min = Number.parseFloat(this.swiper.min);
    const percent = (sliderValue - min) / (max - min);
    const offset = percent * this.swiper.offsetWidth;
    this.closeSwiperButton.style.left = `${offset}px`;
  }

  private setSelectLayerStyle() {
    this.selectionLayerStyle = new Style({
      stroke: new Stroke({
        color: this.config.selection.defaultStrokeColor,
        width: this.config.selection.defaultStrokeWidth
      }),
      fill: new Fill({ color: this.config.selection.defaultFillColor }),
      image: new Circle({
        radius: 7,
        fill: new Fill({ color: this.config.selection.defaultFillColor }),
        stroke: new Stroke({
          color: this.config.selection.defaultStrokeColor,
          width: this.config.selection.defaultStrokeWidth
        })
      })
    });
  }

  private setHighlightLayerStyle() {
    this.highlightLayer.setStyle(
      new Style({
        stroke: new Stroke({
          color: this.config.selection.highlightStrokeColor,
          width: this.config.selection.defaultStrokeWidth
        }),
        fill: new Fill({ color: this.config.selection.highlightFillColor }),
        image: new Circle({
          radius: 7,
          fill: new Fill({ color: this.config.selection.highlightFillColor }),
          stroke: new Stroke({
            color: this.config.selection.highlightStrokeColor,
            width: this.config.selection.defaultStrokeWidth
          })
        })
      })
    );
  }

  private setCrosshairStyle() {
    this.crosshairLayer.setStyle(
      new Style({
        image: new RegularShape({
          fill: new Fill({ color: this.config.selection.defaultFillColor }),
          stroke: new Stroke({ color: this.config.selection.defaultStrokeColor, width: 2 }),
          points: 4,
          radius: 10,
          radius2: 0,
          angle: 0
        })
      })
    );
  }

  onFeaturesSelected(features: Feature[]) {
    this.selectedFeaturesCollection.clear();
    if (features) {
      for (const feature of features) {
        this.selectedFeaturesCollection.push(feature);
      }
      // Recreate the style in case user preferences have changed in the meantime
      this.setSelectLayerStyle();
    }
  }

  onFeatureHighlighted(features: Feature[]) {
    this.highlightedFeaturesCollection.clear();
    for (const feature of features) {
      this.highlightedFeaturesCollection.push(feature);
    }
    // Recreate the style in case user preferences have changed in the meantime
    this.setHighlightLayerStyle();
  }

  zoomToExtent(extent: Extent) {
    this.olMap.getView().fit(extent);
  }

  onChangeProjection(_oldSrid: string, newSrid: string) {
    const newView = this.viewManager.getViewConvertedToSrid(newSrid);
    this.olMap.setView(newView);
  }

  onChangeDarkMode() {
    if (this.state.interface.darkMapMode) {
      this.mapTarget.classList.add('darkmap');
    } else {
      this.mapTarget.classList.remove('darkmap');
    }
  }

  private async onLayerToggled(layer: Layer) {
    if (layer instanceof Layer) {
      if (layer.active) {
        await this.onAddLayers([layer]);
      } else {
        this.onRemoveLayers([layer]);
      }
    }
  }

  private async onAddLayers(layerInfos: Layer[]) {
    for (const l of layerInfos) {
      if (l instanceof LayerWms) {
        this.context.wmsManager.getClient(l).addLayer(l);
      } else if (l instanceof LayerWmts) {
        await this.wmtsManager.addLayer(l);
      } else if (l instanceof LayerLocalFile) {
        this.context.localFileManager.addLayer(l);
      } else if (l instanceof LayerCog) {
        this.cogManager.addLayer(l);
      } else if (l instanceof LayerXYZ) {
        this.xyzManager.addLayer(l);
      } else if (l instanceof LayerDrawing) {
        this.drawingManager.addLayer(l);
      }
    }
  }

  onRemoveLayers(layerInfos: Layer[]) {
    for (const l of layerInfos) {
      if (l instanceof LayerWms) {
        this.context.wmsManager.getClient(l).removeLayer(l);
      } else if (l instanceof LayerWmts) {
        this.wmtsManager.removeLayer(l);
      } else if (l instanceof LayerLocalFile) {
        this.context.localFileManager.removeLayer(l);
      } else if (l instanceof LayerCog) {
        this.cogManager.removeLayer(l);
      } else if (l instanceof LayerXYZ) {
        this.xyzManager.removeLayer(l);
      } else if (l instanceof LayerDrawing) {
        this.drawingManager.removeLayer(l);
      }
    }
  }

  onChangeOrder = debounce(() => this.reorderLayers(), 0);
  private reorderLayers() {
    this.wmtsManager.refreshZIndexes();
    this.context.wmsManager.refreshZIndexes();
  }

  private onChangeLayerOpacity(layerInfos: BaseLayer) {
    if (layerInfos instanceof LayerWms) {
      this.context.wmsManager.getClient(layerInfos).changeOpacity(layerInfos);
    } else if (layerInfos instanceof LayerWmts) {
      if (this.wmtsManager.layerExists(layerInfos)) {
        this.wmtsManager.changeOpacity(layerInfos);
      }
    } else if (layerInfos instanceof LayerLocalFile) {
      this.context.localFileManager.changeOpacity(layerInfos);
    } else if (layerInfos instanceof LayerDrawing) {
      this.drawingManager.changeOpacity(layerInfos);
    } else if (layerInfos instanceof LayerOsm) {
      this.osmManager.changeOpacity(layerInfos);
    } else if (layerInfos instanceof GroupLayer) {
      this.context.stateManager.batchChanges(() => {
        this.setOpacityRecursively(layerInfos.opacity, layerInfos);
      });
    } else {
      console.warn(`Changing opacity for layer ${layerInfos.name} of type ${typeof layerInfos} not supported`);
    }
  }

  private setOpacityRecursively(opacity: number, layer: BaseLayer) {
    if (layer instanceof Layer) {
      layer.opacity = opacity;
    } else if (layer instanceof GroupLayer) {
      for (const childLayer of layer.children) {
        this.setOpacityRecursively(opacity, childLayer);
      }
    }
  }

  private onChangeBasemapOpacity(basemap: Basemap) {
    applyOpacityToLayers(basemap.opacity, basemap.layersList, (layer: BaseLayer) => this.onChangeLayerOpacity(layer));
  }

  /**
   * Change filter configuration on layer (only LayerWMS are affected)
   */
  private onChangeFilter(layerInfos: LayerWms) {
    this.context.wmsManager.getClient(layerInfos).changeFilter(layerInfos);
  }

  /**
   * Change time configuration on layer (only LayerWMS are affected)
   */
  private onChangeTime(layer: GroupLayer | LayerWms) {
    if (layer instanceof LayerWms && layer.active) {
      this.context.wmsManager.getClient(layer).changeTimeRestriction(layer);
    } else if (layer instanceof GroupLayer) {
      this.context.stateManager.batchChanges(() => {
        // Recursively apply the time restriction to all children of the group layer
        this.setTimeRestrictionOnChildren(layer, layer.timeRestriction);
      });
    }
  }

  private setTimeRestrictionOnChildren(layer: GroupLayer, newTime: string | undefined) {
    for (const childLayer of layer.children) {
      if (childLayer instanceof GroupLayer) {
        this.setTimeRestrictionOnChildren(childLayer, newTime);
      } else if (isTimeAwareLayer(childLayer) && childLayer.timeRestriction !== newTime) {
        childLayer.timeRestriction = newTime;
      }
    }
  }

  removeAllBasemapLayers() {
    this.wmtsManager.removeAllBasemapLayers();
    this.context.wmsManager.removeAllBasemapLayers();
    this.osmManager.removeAllBasemapLayers();
    this.cogManager.removeAllBasemapLayers();
    this.xyzManager.removeAllBasemapLayers();
    this.vectorTilesManager.removeAllBasemapLayers();
  }

  onChangeBasemaps(basemaps: Basemap[]) {
    this.removeAllBasemapLayers();

    for (const layer of basemaps.flatMap((basemap) => basemap.layersList)) {
      if (layer instanceof LayerOsm) {
        this.osmManager.addBasemapLayer(layer);
      } else if (layer instanceof LayerVectorTiles) {
        this.vectorTilesManager.addBasemapLayer(layer);
      } else if (layer instanceof LayerCog) {
        this.cogManager.addBasemapLayer(layer);
      } else if (layer instanceof LayerXYZ) {
        this.xyzManager.addBasemapLayer(layer);
      } else if (layer instanceof LayerWmts) {
        this.wmtsManager.addBasemapLayer(layer);
      } else if (layer instanceof LayerWms) {
        this.context.wmsManager.getClient(layer).addBasemapLayer(layer);
      } else {
        throw new TypeError('Unknown basemap type');
      }
    }

    // Apply default opacity
    for (const basemap of basemaps) {
      if (!basemap.opacityDisabled) {
        this.onChangeBasemapOpacity(basemap);
      }
    }
  }

  /**
   * This method checks for the presence of an initial selection originating from a shared state.
   * If present, it applies the query-based or bbox-based selection to the map.
   */
  private applyFeatureSelectionFromSharedState() {
    if (this.state.selection.initialSelectionQuery) {
      const layerName = this.state.selection.initialSelectionQuery.layerName;
      const query = this.state.selection.initialSelectionQuery.query;
      const layer = this.context.layerManager.getTreeItemByLayerName(layerName);
      if (layer && layer instanceof LayerWms) {
        this.selectFeaturesByQuery(layer, query, false);
      }
    } else if (this.state.selection.initialSelectionBox) {
      this.select(this.state.selection.initialSelectionBox);
    }
  }

  /**
   * Applies a feature selection based on the query parameters present in the URL starting with `wfs_`.
   * If the specified layer does not exist in the current tree, it's fetched from the themes and added to the tree.
   * If the layer is resolution-restricted, the map is zoomed to a resolution where the layer is visible.
   */
  private applyFeatureSelectionFromPermalink() {
    const featureSelectionFromUrl = this.context.permalinkManager.getFeatureSelectionQuery();
    if (!featureSelectionFromUrl) {
      return;
    }
    let layerInTree = this.context.layerManager.getTreeItemByLayerName(featureSelectionFromUrl.layer);

    if (!layerInTree) {
      const layer = this.context.themesHelper.findLayerByName(featureSelectionFromUrl.layer);
      if (!layer) {
        throw new Error(`Layer ${featureSelectionFromUrl.layer} cannot be found`);
      }
      const clonedTheme = this.context.themesHelper.getMinimalClonedThemeForLayer(layer);
      clonedTheme.isExpanded = true;
      this.context.themesHelper.mergeThemeInLayerTree(clonedTheme, true, true);
      // Now, get the tree item
      layerInTree = this.context.layerManager.getTreeItemByLayerName(featureSelectionFromUrl.layer);
    }

    if (!(layerInTree && layerInTree instanceof LayerWms && layerInTree.wfsQueryable)) {
      throw new Error(
        `Can't apply feature selection from permalink, layer ${featureSelectionFromUrl.layer} does not exist or does not support querying`
      );
    }

    // Make sure the layer is rendered in the map, otherwise WFS querying won't work
    const currentResolution = this.olMap.getView().getResolution();
    if (layerInTree.minResolution && currentResolution && layerInTree.minResolution > currentResolution) {
      this.state.position.resolution = layerInTree.minResolution;
    } else if (layerInTree.maxResolution && currentResolution && layerInTree.maxResolution < currentResolution) {
      this.state.position.resolution = layerInTree.maxResolution;
    }
    if (layerInTree.activeState !== 'on') {
      this.context.layerManager.toggleLayer(layerInTree, 'on');
    }

    // Create a wfs filter expression combining all filter conditions with an 'AND' operator
    const query = new WfsFilter(
      featureSelectionFromUrl.properties.map(
        (property) =>
          // Note: PropertyType 'string' will result in correct WFS filters for both strings and numbers
          new WfsFilterCondition(property.name, 'eq', property.value, '', 'string')
      ),
      'and'
    );

    this.selectFeaturesByQuery(layerInTree, query);
  }

  /**
   * Selects features based on the given list of wfs filters for a specified WMS layer
   * and optionally moves the map to the selection. The WFS client combines the WFS filters with an AND operator.
   */
  private selectFeaturesByQuery(layerInTree: LayerWms, query: WfsFilter, moveMapToSelection: boolean = true) {
    // Listen for the completed features selection, then move the map to the selected features
    if (moveMapToSelection) {
      const selectSubscription = this.subscribe('selection.selectedFeatures', (_, newFeatures: Feature[]) => {
        this.unsubscribe(selectSubscription);
        this.centerMapOnFeatures(newFeatures);
      });
    }

    // Trigger the selection
    this.context.wmsManager.selectFeaturesByQuery(query, layerInTree);
  }

  /**
   * Centers the map view on the given features. If the features' extent doesn't fit in the current zoom level,
   * the zoom level is adjusted.
   */
  private centerMapOnFeatures(features: Feature[]) {
    const selectedGeometries = new GeometryCollection(
      features.map((f) => f.getGeometry()).filter((f) => f != undefined)
    );
    const featuresExtent = selectedGeometries.getExtent();
    const mapExtent = this.olMap.getView().calculateExtent();
    if (getWidth(featuresExtent) <= getWidth(mapExtent) && getHeight(featuresExtent) <= getHeight(mapExtent)) {
      // Move the map so all selected features are visible -> this won't change the zoom level
      this.state.position.center = getCenter(featuresExtent);
    } else {
      // Fit the map view so all features are visible -> this will change the zoom level
      this.zoomToExtent(featuresExtent);
    }
  }

  /**
   * Moves the map to the position defined in the permalink, making sure the map is initialized and ready to be moved.
   */
  private applyMapPositionFromPermalink() {
    const position = this.context.permalinkManager.getMapPosition(this.projection);
    if (position?.isValid) {
      // We need the following to recalculate resolution and scale to properly update the position state
      this.state.position = position;
      if (position.markers.length > 0) {
        // Add marker to the map
        this.addMarker(position.markers[0]);
      }
    }
  }

  private clearAllMarkers() {
    this.markerSource.clear();
  }

  private addMarker(mapMarker: MapMarker) {
    /*
     In OpenLayers we need to calculate the anchor based on the marker size and offset as it is `fraction` by default.
     As the offset and the size are provided in pixel, we need to calculate the fraction for each axis. So, for example,
     when you have size [21,25] and offset [-10.5,-25] the anchor must be [0.5, 1] to have the bottom center of the
     marker at the given coordinates.
     */
    const anchor =
      mapMarker.offset && mapMarker.size
        ? [Math.abs(mapMarker.offset[0] / mapMarker.size[0]), Math.abs(mapMarker.offset[1] / mapMarker.size[1])]
        : undefined;
    const iconStyle = new Style({
      image: new Icon({
        anchor: anchor,
        src: mapMarker.imageUrl,
        width: mapMarker.size ? mapMarker.size[0] : undefined,
        height: mapMarker.size ? mapMarker.size[1] : undefined
      })
    });
    const marker = new Feature({
      geometry: new Point(mapMarker.position)
    });
    marker.setStyle(iconStyle);
    marker.set('mapMarker', mapMarker);
    this.markerSource.addFeature(marker);
  }

  private showCrosshair(position: MapPosition) {
    if (!position.crosshair) {
      return;
    }

    // Remove existing crosshair first
    if (this.crosshairFeature) {
      this.olMap.removeLayer(this.crosshairLayer);
    }

    // Set new crosshair
    this.crosshairFeature = new Feature(new Point(position.crosshair));
    this.crosshairLayer = new VectorLayer({ source: new VectorSource({ features: [this.crosshairFeature] }) });
    this.setCrosshairStyle();
    this.olMap.addLayer(this.crosshairLayer);
  }

  private onFeaturesTypeChanged(_newFeaturesType: string | undefined) {
    console.log('Features type changed: ' + _newFeaturesType);
    this.selectionLayer.changed();
  }
}
